import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, Search, Plus, Check } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

/**
 * BulkAssignLoteModal — escolher um lote existente ou criar um novo,
 * e aplicar `imoveis.lote_id = X` nos imóveis selecionados.
 *
 * Props:
 *   imovelIds: string[] — UUIDs dos imóveis selecionados
 *   onClose(): void
 *   onApplied(): void  // chamado após UPDATE bem-sucedido
 */
export default function BulkAssignLoteModal({ imovelIds, onClose, onApplied }) {
  const { user } = useAuth();
  const [lotes, setLotes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedLoteId, setSelectedLoteId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newLoteName, setNewLoteName] = useState('');
  const [newLoteAreaId, setNewLoteAreaId] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lR, aR] = await Promise.all([
          supabase.from('lotes')
            .select('id, nome, area_id, areas:area_id(id, nome)')
            .order('nome'),
          supabase.from('areas').select('id, nome').order('nome'),
        ]);
        if (cancelled) return;
        if (lR.error) throw lR.error;
        if (aR.error) throw aR.error;
        setLotes(lR.data || []);
        setAreas(aR.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lotes;
    return lotes.filter((l) =>
      [l.nome, l.areas?.nome].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [lotes, query]);

  const handleApply = async () => {
    if (!selectedLoteId || imovelIds.length === 0) return;
    if (imovelIds.length > 25) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Você está vinculando ${imovelIds.length} imóveis ao mesmo lote. Confirmar?`,
      );
      if (!ok) return;
    }
    setApplying(true);
    setError(null);
    try {
      const { error: upErr } = await supabase
        .from('imoveis')
        .update({
          lote_id: selectedLoteId,
          updated_at: new Date().toISOString(),
          updated_by: user?.email || 'ui_bulk_assign_lote',
        })
        .in('id', imovelIds);
      if (upErr) throw upErr;
      onApplied?.();
      onClose?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setApplying(false);
    }
  };

  const handleCreateLote = async () => {
    const nome = newLoteName.trim();
    if (!nome || !newLoteAreaId) {
      setError('Nome e área são obrigatórios para criar um lote.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const { data, error: cErr } = await supabase
        .from('lotes')
        .insert({
          nome,
          area_id: newLoteAreaId,
          updated_by: user?.email || 'ui_bulk_create_lote',
        })
        .select('id, nome, area_id, areas:area_id(id, nome)')
        .single();
      if (cErr) throw cErr;
      setLotes((prev) => [...prev, data].sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
      setSelectedLoteId(data.id);
      setShowCreate(false);
      setNewLoteName('');
      setNewLoteAreaId('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !applying && !creating) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: '85vh' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-800">
            Vincular {imovelIds.length} imóve{imovelIds.length > 1 ? 'is' : 'l'} a um lote
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={applying || creating}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
              <Loader2 size={14} className="animate-spin" /> Carregando lotes...
            </div>
          )}

          {!loading && !showCreate && (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar lote ou área..."
                  className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
                  autoFocus
                />
              </div>

              {filtered.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-4 text-center">
                  {query ? 'Nenhum lote bate com a busca.' : 'Nenhum lote cadastrado.'}
                </div>
              ) : (
                <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
                  {filtered.map((l) => {
                    const isSel = selectedLoteId === l.id;
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setSelectedLoteId(l.id)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                          isSel ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{l.nome || '(sem nome)'}</div>
                          {l.areas?.nome && (
                            <div className="text-[10px] text-slate-500 truncate">Área: {l.areas.nome}</div>
                          )}
                        </div>
                        {isSel && <Check size={14} className="text-blue-600 flex-shrink-0 ml-2" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => { setShowCreate(true); setError(null); }}
                className="w-full text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 py-1.5 border border-dashed border-blue-200 rounded hover:bg-blue-50/50"
              >
                <Plus size={12} /> Criar novo lote
              </button>
            </>
          )}

          {!loading && showCreate && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Novo lote</div>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-600 mb-1">Nome *</span>
                <input
                  type="text"
                  value={newLoteName}
                  onChange={(e) => setNewLoteName(e.target.value)}
                  placeholder="ex: Quadra 050.022 — Ipiranga"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:border-blue-400"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-600 mb-1">Área *</span>
                <select
                  value={newLoteAreaId}
                  onChange={(e) => setNewLoteAreaId(e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-blue-400"
                >
                  <option value="">— selecione —</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setError(null); }}
                  disabled={creating}
                  className="flex-1 px-3 py-1.5 text-xs bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleCreateLote}
                  disabled={creating || !newLoteName.trim() || !newLoteAreaId}
                  className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {creating && <Loader2 size={11} className="animate-spin" />}
                  Criar e selecionar
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800">{error}</div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={applying || creating}
            className="px-3 py-1.5 text-xs bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={applying || creating || !selectedLoteId}
            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            {applying && <Loader2 size={12} className="animate-spin" />}
            {applying ? 'Vinculando...' : `Vincular (${imovelIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
